import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Scale, Cpu, Quote, Check } from "lucide-react";
import SEO from "../components/SEO";
import {
  firmOverview, firmStats, vision, mission, coreValues,
  partners, journey, litigationAreas, nonLitigationServices, forums,
  technology, serviceCommitment, memberships, proBono, clientSectors,
  whyChooseUs, clientLogos, profileImages,
} from "../data/firm";

const sectionBg = ({ dark, bone }) => {
  if (dark) return "bg-ink text-white";
  if (bone) return "bg-bone border-y border-rule";
  return "bg-paper";
};

const Section = ({ kicker, title, children, dark, bone, id, testid }) => (
  <section
    id={id}
    data-testid={testid}
    className={`py-14 md:py-28 ${sectionBg({ dark, bone })}`}
  >
    <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
      {kicker && <p className={`kicker mb-5 ${dark ? "text-white/70" : ""}`}>{kicker}</p>}
      {title && (
        <h2 className={`font-display text-3xl md:text-5xl leading-tight ${dark ? "text-white" : "text-ink"} max-w-4xl`}>
          {title}
        </h2>
      )}
      <div className="mt-12">{children}</div>
    </div>
  </section>
);

export default function CompanyProfile() {
  return (
    <>
      <SEO
        title="Company Profile"
        description="The complete company profile of K & K Associates — a full-service law firm in New Delhi established in 2006. Founders, vision & mission, 18 litigation and 9 non-litigation practice areas, courts & forums, technology, clientele, memberships and pro bono work."
        canonical="https://kandkassociates.com/company-profile"
        keywords={["K and K Associates company profile", "law firm New Delhi profile", "Manav Kumar advocate", "Vatsal Kumar advocate", "full service law firm Delhi"]}
        schema={{
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Organization",
              name: "K & K Associates",
              url: "https://kandkassociates.com/company-profile",
              foundingDate: "2006",
              description: firmOverview,
              founder: partners.map((p) => ({ "@type": "Person", name: p.name, jobTitle: p.role })),
            },
            {
              "@type": "BreadcrumbList",
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: "https://kandkassociates.com/" },
                { "@type": "ListItem", position: 2, name: "Company Profile" },
              ],
            },
          ],
        }}
      />

      {/* HERO */}
      <section className="relative pt-28 md:pt-40 pb-14 md:pb-20 bg-ink text-white overflow-hidden" data-testid="profile-hero">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
          <div className="lg:col-span-8">
            <p className="kicker text-white/70 mb-6">Company Profile · Estd. 2006</p>
            <h1 className="font-display text-4xl sm:text-5xl md:text-6xl xl:text-7xl leading-[1] sm:leading-[0.98]" style={{ fontWeight: 400 }}>
              K &amp; K Associates
              <span className="block italic text-white/70 text-2xl sm:text-3xl md:text-4xl mt-3 md:mt-4" style={{ fontWeight: 300 }}>
                Advocates &amp; Solicitors · New Delhi
              </span>
            </h1>
          </div>
          <div className="lg:col-span-4">
            <p className="font-serif text-lg md:text-xl text-white/85 leading-relaxed">{firmOverview}</p>
          </div>
        </div>
      </section>

      {/* CREDENTIALS STRIP */}
      <section className="bg-brand text-white" data-testid="profile-stats">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 grid grid-cols-2 md:grid-cols-4">
          {firmStats.map((s, i) => (
            <div key={s.label} className={`py-10 ${i !== 0 ? "md:border-l border-white/20" : ""} ${i % 2 === 1 ? "border-l border-white/20" : ""}`}>
              <p className="font-display text-4xl md:text-5xl">{s.num}</p>
              <p className="kicker text-white/70 mt-2">{s.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* VISION & MISSION */}
      <Section kicker="Vision & Mission" bone testid="profile-vision-mission">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-rule border border-rule">
          <div className="bg-paper p-10 md:p-12">
            <h3 className="font-display text-3xl text-brand mb-5">Our Vision</h3>
            <p className="font-serif text-xl text-ink/80 leading-relaxed">{vision}</p>
          </div>
          <div className="bg-paper p-10 md:p-12">
            <h3 className="font-display text-3xl text-brand mb-5">Our Mission</h3>
            <p className="font-serif text-xl text-ink/80 leading-relaxed">{mission}</p>
          </div>
        </div>
      </Section>

      {/* CORE VALUES */}
      <Section kicker="Core Values" title={<>The principles that <span className="italic text-brand">guide every engagement.</span></>} testid="profile-values">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-rule border border-rule">
          {coreValues.map((v) => (
            <div key={v.title} className="bg-paper p-10">
              <h3 className="font-display text-2xl text-ink mb-4">{v.title}</h3>
              <p className="font-serif text-base text-ink/70 leading-relaxed">{v.body}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* PARTNERS */}
      <Section kicker="The Partners" bone title={<>Counsel led from the <span className="italic text-brand">front.</span></>} testid="profile-partners">
        <div className="space-y-px bg-rule border border-rule">
          {partners.map((p) => (
            <div key={p.name} className="bg-paper p-10 md:p-12 grid grid-cols-1 lg:grid-cols-12 gap-10">
              <div className="lg:col-span-4">
                <div className="aspect-[4/5] overflow-hidden bg-bone mb-6 border border-rule">
                  <img src={p.image} alt={p.name} className="w-full h-full object-cover object-top" data-testid={`partner-photo-${p.name.split(" ").pop().toLowerCase()}`} />
                </div>
                <p className="kicker text-brand mb-3">{p.role}</p>
                <h3 className="font-display text-3xl md:text-4xl text-ink leading-tight">{p.name}</h3>
                <p className="font-serif text-lg text-ink/70 mt-3">{p.experience}</p>
                <div className="fine-rule w-16 my-6" />
                <ul className="space-y-2">
                  {p.education.map((e) => (
                    <li key={e} className="flex items-start gap-2 font-serif text-sm text-ink/75">
                      <Check className="w-4 h-4 mt-1 shrink-0 text-brand" strokeWidth={1.5} />{e}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="lg:col-span-8 font-serif text-lg text-ink/80 leading-relaxed space-y-5">
                {p.bio.map((para) => <p key={para.slice(0, 40)}>{para}</p>)}
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* JOURNEY */}
      <Section kicker="Our Journey" title={<>A practice built <span className="italic text-brand">since 2006.</span></>} testid="profile-journey">
        <div className="border-l-2 border-brand/30 ml-2">
          {journey.map((j) => (
            <div key={j.year} className="relative pl-10 pb-10 last:pb-0">
              <span className="absolute -left-[7px] top-2 w-3 h-3 rounded-full bg-brand" />
              <p className="font-display text-2xl text-brand">{j.year}</p>
              <p className="font-serif text-lg text-ink/75 leading-relaxed mt-2 max-w-3xl">{j.text}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* PRACTICE AREAS */}
      <Section kicker="Practice Areas" bone title={<>Eighteen litigation areas. <span className="italic text-brand">Nine advisory services.</span></>} testid="profile-practice-areas">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div>
            <div className="flex items-center gap-3 mb-6">
              <Scale className="w-6 h-6 text-brand" strokeWidth={1.5} />
              <h3 className="font-display text-2xl text-ink">Litigation Practice Areas</h3>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 border-t border-l border-rule">
              {litigationAreas.map((a) => (
                <div key={a} className="border-r border-b border-rule p-4 font-serif text-base text-ink/85 bg-paper hover:bg-bone transition-colors">{a}</div>
              ))}
            </div>
          </div>
          <div>
            <div className="flex items-center gap-3 mb-6">
              <Cpu className="w-6 h-6 text-brand" strokeWidth={1.5} />
              <h3 className="font-display text-2xl text-ink">Non-Litigation Services</h3>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 border-t border-l border-rule">
              {nonLitigationServices.map((a) => (
                <div key={a} className="border-r border-b border-rule p-4 font-serif text-base text-ink/85 bg-paper hover:bg-bone transition-colors">{a}</div>
              ))}
            </div>
          </div>
        </div>
        <Link to="/services" className="btn-outline mt-12 inline-flex" data-testid="profile-services-link">
          Explore detailed practice pages <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.5} />
        </Link>
      </Section>

      {/* TRACK RECORD removed per client request */}

      {/* COURTS & FORUMS */}
      <Section kicker="Courts & Judicial Forums" bone title={<>Present where <span className="italic text-brand">it matters.</span></>} testid="profile-forums">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div>
            <h3 className="font-display text-2xl text-ink mb-6">Primary Forums — Delhi</h3>
            <ul className="space-y-3">
              {forums.primary.map((f) => (
                <li key={f} className="flex items-start gap-3 font-serif text-lg text-ink/80">
                  <Check className="w-5 h-5 mt-1 shrink-0 text-brand" strokeWidth={1.5} />{f}
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h3 className="font-display text-2xl text-ink mb-6">Extended Reach — Pan-India (12 States/UTs)</h3>
            <div className="flex flex-wrap gap-2">
              {forums.panIndia.map((f) => (
                <span key={f} className="border border-rule px-4 py-2 font-serif text-base text-ink/80 bg-paper">{f}</span>
              ))}
            </div>
          </div>
        </div>
      </Section>

      {/* TECHNOLOGY */}
      <Section kicker="Technology & Operations" title={<>A modern firm, <span className="italic text-brand">end to end.</span></>} testid="profile-technology">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-rule border border-rule">
          {technology.map((t) => (
            <div key={t.title} className="bg-paper p-10">
              <h3 className="font-display text-2xl text-ink mb-4">{t.title}</h3>
              <p className="font-serif text-base text-ink/70 leading-relaxed">{t.body}</p>
            </div>
          ))}
        </div>
        <div className="mt-12">
          <p className="kicker mb-6">Client Service Commitment</p>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-px bg-rule border border-rule">
            {serviceCommitment.map((s, i) => (
              <div key={s.step} className="bg-paper p-8">
                <p className="font-display text-4xl text-brand mb-3">{String(i + 1).padStart(2, "0")}</p>
                <h4 className="font-display text-xl text-ink mb-2">{s.step}</h4>
                <p className="font-serif text-sm text-ink/70 leading-relaxed">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* CLIENTELE */}
      <Section kicker="Clientele" bone title={<>Trusted by leaders across <span className="italic text-brand">every sector.</span></>} testid="profile-clientele">
        {/* Logo wall */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-px bg-rule border border-rule mb-12" data-testid="client-logo-wall">
          {clientLogos.map((src, i) => (
            <div key={src} className="bg-white flex items-center justify-center p-5 md:p-6 aspect-[3/2]">
              <img
                src={src}
                alt={`Client ${i + 1}`}
                loading="lazy"
                className="max-h-full max-w-full object-contain opacity-80 hover:opacity-100 transition-opacity"
                data-testid={`client-logo-${i + 1}`}
              />
            </div>
          ))}
        </div>
        {/* Sector breakdown */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-rule border border-rule">
          {clientSectors.map((cs) => (
            <div key={cs.sector} className="bg-paper p-8 md:p-10">
              <p className="kicker text-brand mb-4">{cs.sector}</p>
              <p className="font-serif text-lg text-ink/80 leading-relaxed">{cs.clients.join(" · ")}</p>
            </div>
          ))}
        </div>
        <div className="mt-12 border border-rule bg-white p-4 md:p-6">
          <img src={profileImages.sectorRepresentation} alt="Sector representation overview" loading="lazy" className="w-full h-auto" data-testid="sector-representation-img" />
        </div>
      </Section>

      {/* DISPUTE RESOLUTION APPROACH */}
      <Section kicker="Our Approach" title={<>Resolution before <span className="italic text-brand">litigation.</span></>} testid="profile-dispute-approach">
        <p className="font-serif text-lg text-ink/75 leading-relaxed max-w-3xl mb-8">
          We move every matter through a disciplined journey — exhausting early, cost-effective resolution avenues before litigation, and supporting clients well beyond the final order.
        </p>
        <div className="border border-rule bg-white p-4 md:p-6">
          <img src={profileImages.disputeResolution} alt="K & K Associates approach to dispute resolution" loading="lazy" className="w-full h-auto" data-testid="dispute-resolution-img" />
        </div>
      </Section>

      {/* WHY CHOOSE US */}
      <Section kicker="Why Choose K & K" title={<>Six reasons clients <span className="italic text-brand">stay with us.</span></>} testid="profile-why">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-rule border border-rule">
          {whyChooseUs.map((w) => (
            <div key={w.title} className="bg-paper p-10">
              <h3 className="font-display text-2xl text-ink mb-4">{w.title}</h3>
              <p className="font-serif text-base text-ink/70 leading-relaxed">{w.body}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* MEMBERSHIPS & PRO BONO */}
      <Section kicker="Memberships & Pro Bono" bone testid="profile-memberships">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div>
            <h3 className="font-display text-2xl text-ink mb-6">Conferences & Memberships</h3>
            <ul className="space-y-3">
              {memberships.map((m) => (
                <li key={m} className="flex items-start gap-3 font-serif text-base text-ink/80">
                  <Check className="w-4 h-4 mt-1 shrink-0 text-brand" strokeWidth={1.5} />{m}
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h3 className="font-display text-2xl text-ink mb-6">Pro Bono & Legal Aid</h3>
            <p className="font-serif text-base text-ink/75 mb-4">Panel membership: {proBono.panels.join(" · ")}.</p>
            <ul className="space-y-3">
              {proBono.areas.map((a) => (
                <li key={a} className="flex items-start gap-3 font-serif text-base text-ink/80">
                  <Check className="w-4 h-4 mt-1 shrink-0 text-brand" strokeWidth={1.5} />{a}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </Section>

      {/* CTA */}
      <section className="bg-ink text-white py-14 md:py-28">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-8">
            <Quote className="w-10 h-10 text-white/40 mb-6" strokeWidth={1} />
            <h2 className="font-display text-3xl md:text-5xl leading-tight">
              Tell us your matter.<br />
              <span className="italic text-white/70">We will tell you the path forward.</span>
            </h2>
          </div>
          <div className="lg:col-span-4 flex flex-col sm:flex-row lg:flex-col gap-4">
            <Link to="/contact" className="btn-ghost-light !text-white" data-testid="profile-cta-contact">
              Book a Consultation <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.5} />
            </Link>
            <a href="https://wa.me/919643572870" target="_blank" rel="noreferrer" className="btn-primary border-white" data-testid="profile-cta-whatsapp">
              WhatsApp +91 96435 72870
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
